import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  customFieldsApi,
  tasksApi,
  type CustomFieldDefinition,
  type TaskCustomFieldsResponse,
  type CustomFieldType,
} from '@/lib/api';
import type { List, Task } from '@/types';

/** Invalidate only per-task custom-field payloads, not full task detail (avoids drawer/list refetch fan-out). */
function invalidateTaskCustomFieldDetailQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'tasks' &&
        key[1] === 'detail' &&
        typeof key[2] === 'string' &&
        key[3] === 'custom-fields'
      );
    },
  });
}

function getCachedListIdsForProject(queryClient: QueryClient, projectId: string): string[] {
  const ids = new Set<string>();
  for (const [, data] of queryClient.getQueriesData<List[]>({ queryKey: ['lists', projectId] })) {
    if (!Array.isArray(data)) continue;
    for (const list of data) {
      if (list?.id) ids.add(list.id);
    }
  }
  return [...ids];
}

/** Project-scoped definition changes affect effective fields + list value maps for each list in the project. */
function invalidateListDerivedCustomFieldCaches(queryClient: QueryClient, projectId: string) {
  const listIds = getCachedListIdsForProject(queryClient, projectId);
  if (listIds.length > 0) {
    for (const listId of listIds) {
      queryClient.invalidateQueries({ queryKey: ['custom-fields', 'effective', listId] });
      queryClient.invalidateQueries({ queryKey: ['custom-fields', 'list-values', listId] });
    }
  } else {
    // No list cache (e.g. manage dialog without visiting lists) — fall back to prefix match for active queries
    queryClient.invalidateQueries({ queryKey: ['custom-fields', 'effective'] });
    queryClient.invalidateQueries({ queryKey: ['custom-fields', 'list-values'] });
  }
}

/** Optional gate for definition queries (e.g. only fetch when Manage dialog is open). Defaults to true. */
export type CustomFieldDefinitionsQueryOptions = {
  queryEnabled?: boolean;
};

export function useProjectCustomFields(
  projectId: string | undefined,
  options?: CustomFieldDefinitionsQueryOptions,
) {
  const queryEnabled = options?.queryEnabled ?? true;
  return useQuery({
    queryKey: ['custom-fields', 'project', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const res = await customFieldsApi.getProjectFields(projectId);
      return res.data as CustomFieldDefinition[];
    },
    enabled: !!projectId && queryEnabled,
  });
}

export function useListCustomFields(listId: string | undefined, options?: CustomFieldDefinitionsQueryOptions) {
  const queryEnabled = options?.queryEnabled ?? true;
  return useQuery({
    queryKey: ['custom-fields', 'list', listId],
    queryFn: async () => {
      if (!listId) throw new Error('List ID required');
      const res = await customFieldsApi.getListFields(listId);
      return res.data as CustomFieldDefinition[];
    },
    enabled: !!listId && queryEnabled,
  });
}

export function useEffectiveCustomFieldsForList(listId: string | undefined) {
  return useQuery({
    queryKey: ['custom-fields', 'effective', listId],
    queryFn: async () => {
      if (!listId) throw new Error('List ID required');
      const res = await customFieldsApi.getEffectiveForList(listId);
      return res.data as CustomFieldDefinition[];
    },
    enabled: !!listId,
    staleTime: 30_000,
  });
}

export function useTaskCustomFieldValuesForList(listId: string | undefined) {
  return useQuery({
    queryKey: ['custom-fields', 'list-values', listId],
    queryFn: async () => {
      if (!listId) throw new Error('List ID required');
      const res = await customFieldsApi.getTaskCustomFieldValuesForList(listId);
      return res.data as Record<string, Record<string, unknown>>;
    },
    enabled: !!listId,
    staleTime: 30_000,
  });
}

export function useTaskCustomFields(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'detail', taskId, 'custom-fields'],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID required');
      const res = await tasksApi.getCustomFields(taskId);
      return res.data as TaskCustomFieldsResponse;
    },
    enabled: !!taskId,
  });
}

export function useUpdateTaskCustomFields(taskId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      if (!taskId) throw new Error('Task ID required');
      const res = await tasksApi.updateCustomFields(taskId, values);
      return res.data as TaskCustomFieldsResponse;
    },
    onSuccess: (data) => {
      if (!taskId) return;
      queryClient.setQueryData<TaskCustomFieldsResponse>(['tasks', 'detail', taskId, 'custom-fields'], data);
      const cachedTask = queryClient.getQueryData<Task>(['tasks', 'detail', taskId]);
      if (cachedTask?.listId) {
        void queryClient.invalidateQueries({ queryKey: ['custom-fields', 'list-values', cachedTask.listId] });
      }
    },
  });
}

export function useCreateProjectCustomField(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; key: string; type: CustomFieldType; config?: Record<string, unknown>; position?: number }) => {
      const res = await customFieldsApi.createProjectField(projectId, data);
      return res.data as CustomFieldDefinition;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields', 'project', projectId] });
    },
  });
}

export function useCreateListCustomField(listId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; key: string; type: CustomFieldType; config?: Record<string, unknown>; position?: number }) => {
      const res = await customFieldsApi.createListField(listId, data);
      return res.data as CustomFieldDefinition;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields', 'list', listId] });
      queryClient.invalidateQueries({ queryKey: ['custom-fields', 'effective', listId] });
      queryClient.invalidateQueries({ queryKey: ['custom-fields', 'list-values', listId] });
    },
  });
}

export function useUpdateCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; config?: Record<string, unknown>; position?: number; isArchived?: boolean };
    }) => {
      const res = await customFieldsApi.updateField(id, data);
      return res.data as CustomFieldDefinition;
    },
    onSuccess: (updated) => {
      if (updated.scopeType === 'project' && updated.projectId) {
        const pid = updated.projectId;
        queryClient.invalidateQueries({ queryKey: ['custom-fields', 'project', pid] });
        invalidateListDerivedCustomFieldCaches(queryClient, pid);
      } else if (updated.scopeType === 'list' && updated.listId) {
        const lid = updated.listId;
        queryClient.invalidateQueries({ queryKey: ['custom-fields', 'list', lid] });
        queryClient.invalidateQueries({ queryKey: ['custom-fields', 'effective', lid] });
        queryClient.invalidateQueries({ queryKey: ['custom-fields', 'list-values', lid] });
      } else {
        // Defensive: unknown scope — avoid stale UI
        queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      }
      invalidateTaskCustomFieldDetailQueries(queryClient);
    },
  });
}

export function useDeleteCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await customFieldsApi.deleteField(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      invalidateTaskCustomFieldDetailQueries(queryClient);
    },
  });
}
